// ---------------------------------------------
// OpenWeather EU AQI scale (1–5)
// ---------------------------------------------

/**
 * Air Quality Index (1–5 scale)
 * 1 = Good, 2 = Fair, 3 = Moderate, 4 = Poor, 5 = Very Poor
 */
export enum AirQualityLevelEnum {
  Good = 1,
  Fair = 2,
  Moderate = 3,
  Poor = 4,
  VeryPoor = 5,
}

export type OpenWeatherAqiLevelConcern =
  | "Good"
  | "Fair"
  | "Moderate"
  | "Poor"
  | "Very Poor"
  | "Unknown"

// OpenWeather AQI ref slugs
export type OpenWeatherAqiRef =
  | "aqi-good"
  | "aqi-fair"
  | "aqi-moderate"
  | "aqi-poor"
  | "aqi-very-poor"
  | "aqi-unknown"
  | null

// ---------------------------------------------
// US EPA AQI Levels
// ---------------------------------------------

export type UsAqiLevelConcern =
  | "Good"
  | "Moderate"
  | "Unhealthy for sensitive groups"
  | "Unhealthy"
  | "Very unhealthy"
  | "Hazardous"
  | "Very Hazardous"
  | null

export type UsAqiRef =
  | "us-aqi-good"
  | "us-aqi-moderate"
  | "us-aqi-usg"
  | "us-aqi-unhealthy"
  | "us-aqi-very-unhealthy"
  | "us-aqi-hazardous"
  | "us-aqi-very-hazardous"
  | null

/* -------------------------------------------------------
 * RAW RESPONSE STRUCTURES (OpenWeather APIs)
 * ----------------------------------------------------- */

export type Coordinate = {
  lon: number
  lat: number
}

export type WeatherCondition = {
  id: number
  main: string
  description: string
  /** icon code such as '01d', '02n', etc. */
  icon: string
  icon_url: string // custom field from n8n workflow
}

export type MainWeather = {
  temp: number
  feelsLike: number
  tempMin: number
  tempMax: number
  pressure: number
  humidity: number
  seaLevel?: number
  grndLevel?: number
}

export type Wind = {
  speed: number
  deg: number
  gust?: number
}

export type Cloud = {
  all: number
}

export type Precipitation = {
  "1h"?: number
  "3h"?: number
}

export type Sys = {
  type?: number
  id?: number
  country: string
  sunrise: number
  sunset: number
}

/**
 * Current Weather API: https://openweathermap.org/current
 */
export type OpenWeatherCurrent = {
  coord: Coordinate
  weather: WeatherCondition[]
  base: string
  main: MainWeather
  visibility: number
  wind: Wind
  clouds: Cloud
  rain?: Precipitation
  snow?: Precipitation
  dt: number
  sys: Sys
  timezone: number
  id: number
  name: string
  cod: number
}

/**
 * Air Pollution API: https://openweathermap.org/api/air-pollution
 */
export type AirComponents = {
  co: number
  no: number
  no2: number
  o3: number
  so2: number
  pm2_5: number
  pm10: number
  nh3: number
}

export type AirPollutionItem = {
  main: {
    aqi: AirQualityLevelEnum
  }
  components: AirComponents
  dt: number
}

export type OpenWeatherAirPollution = {
  coord: Coordinate
  list: AirPollutionItem[]
}

export type SummaryData = {
  updatedAt: string
  temperature: number | null
  humidity: number | null
  condition: string
  icon_url: string | null

  // OpenWeather scale for Air Quality Index levels
  owm_aqi_level_concern: OpenWeatherAqiLevelConcern
  owm_aqi_ref: OpenWeatherAqiRef
  owm_aqi_value: number | null

  // U.S. EPA AQI
  us_aqi_value: number | null
  us_aqi_level_concern: UsAqiLevelConcern
  us_aqi_ref: UsAqiRef

  city: string | null
  country: string | null
}

/**
 * Combined structure returned from Cloudflare Worker
 * (contains both Current Weather + Air Pollution APIs)
 */
export type CombinedWeatherData = {
  weather: OpenWeatherCurrent
  air: OpenWeatherAirPollution
  summary: SummaryData
  fetchedAt: string
}

export type NormalizedWeatherData = {
  updatedAt: string
  temperature: number | null
  humidity: number | null
  condition: string
  iconUrl: string | null

  // OpenWeather scale for Air Quality Index levels
  owmAqiLevelConcern: OpenWeatherAqiLevelConcern
  owmAqiRef: OpenWeatherAqiRef
  owmAqiValue: number | null

  // U.S. EPA AQI
  usAqiValue: number | null
  usAqiLevelConcern: UsAqiLevelConcern
  usAqiRef: UsAqiRef

  city: string | null
  country: string | null
}
